import { useState } from 'react';
import { useMaestroWorking } from '../../lib/maestro-working';
import { useTranslation } from '../../lib/i18n';
import { useGenericBi } from '../../lib/use-generic-bi';
import { useProductName } from '../../lib/use-brand';
import { ChatFab } from './ChatFab';
import { HomeComposer } from './HomeComposer';
import { useKeyboardInset } from './use-keyboard-inset';

/**
 * Narrow-only chat entry. Closed, it's the corner FAB. Tapping it reveals an
 * input-only composer (auto-focused, pinned above the keyboard) over a dismiss
 * scrim — no chat history, just a place to type. Sending hands off to the
 * full-screen chat: HomeComposer's `continueRecent` routes into the recent
 * (<24h) thread when one exists, else a fresh one. That handoff opens the chat
 * (FloatingChat resolves the URL to `expanded`) and auto-sends the message. We
 * also drop the peek the moment it submits (`onSubmitted`) so the emptied
 * composer — and its disabled send arrow — doesn't linger over the keyboard
 * during the route handoff before the launcher stops rendering.
 */
export function MobileChatLauncher() {
  const { t } = useTranslation();
  const productName = useProductName();
  const { logPageClickBi } = useGenericBi();
  const maestro = useMaestroWorking(false);
  const [peek, setPeek] = useState(false);
  const keyboardInset = useKeyboardInset();

  // The FAB's open click is instrumented (`open_chat`) inside ChatFab itself, so it
  // isn't re-logged here.
  if (!peek) {
    // The FAB is the closed-chat door, so it wears the Maestro's mid-turn dot. Only while the chat is
    // closed — which is the only state this component renders in anyway.
    return (
      <ChatFab
        onClick={() => setPeek(true)}
        label={t('chat.openProduct', { productName })}
        working={maestro.working}
        workingLabel={t('agentsIsland.maestroWorking')}
      />
    );
  }

  return (
    <>
      <div
        onClick={() => {
          logPageClickBi({ page_name: 'chat', button_name: 'dismiss_chat' });
          setPeek(false);
        }}
        aria-hidden
        style={{
          position: 'fixed',
          inset: 0,
          // Drop the scrim's top below the personal-sale banner when it's showing (else 0px) — same
          // treatment as the real chat sheet (`FloatingChat`'s `panelStyle`) — so this peek composer's
          // dismiss-scrim doesn't dim (or sit over) the banner while it's up.
          top: 'var(--personal-sale-banner-h, 0px)',
          zIndex: 50,
          background: 'rgba(var(--ds-black-rgb), 0.12)',
        }}
      />
      <div
        style={{
          position: 'fixed',
          insetInline: 0,
          bottom: keyboardInset,
          zIndex: 51,
          padding: '10px 6px calc(10px + env(safe-area-inset-bottom))',
          background: 'linear-gradient(to top, var(--field-color) 60%, rgba(var(--surface-base-rgb),0))',
          animation: 'orion-sheet-in 220ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <HomeComposer autoFocus continueRecent onSubmitted={() => setPeek(false)} />
      </div>
    </>
  );
}
